import React from "react";
import { useFormContext } from "react-hook-form";

import { RegistrationMode } from "../../../registroIngresoNavigation";
import { NuevaEmpresaData } from "./NuevaEmpresaModal";
import { AuthorizingCompany, SolicitarAutorizacionSubmitData } from "./SolicitarAutorizacionModal";
import { SolicitarAutorizacionEgresoSubmitData } from "./SolicitarAutorizacionEgresoModal";
import { RegistrarAutorizacionSubmitData } from "./RegistrarAutorizacionModal";
import {
  AuthorizationGroup,
  AuthorizationRow,
  ExitAuthorization,
  TransportExitControls,
  TransportFlow,
} from "./transportAuthorizationTypes";
import {
  buildAuthorizationGroups,
  buildExitAuthorizationGroups,
  mergeLocalDockRequests,
  resolveEntranceAuthorizingCompanyId,
  resolveEntranceVehiclePrefill,
  resolveExitVehiclePrefill,
} from "./transportAuthorizationMappers";
import { useTransportCompanies } from "./useTransportCompanies";
import { useTransportAuthorizations } from "./useTransportAuthorizations";
import { TransportCompany, TransportEventType } from "../../../../../../passapp-sdk/types/transportTypes";
import { EntryRegistrationFormValues } from "../../types";
import { parseApiOrIsoDate } from "../../../utils/apiDates";

/** Lo que ambos flujos exponen para prefillear el formulario: `AuthorizationRow` (entrada) y
 * `ExitAuthorization` (egreso) la satisfacen. */
type AuthorizationPrefillSource = Pick<
  AuthorizationRow,
  "vehiclePlate" | "trailerPlate" | "merchandiseInTransit" | "receipt" | "seal"
> & { gateObservations?: string | null };

/** Todo el estado y los handlers del flujo de transporte: elegir empresa, listar/solicitar
 * autorizaciones y aplicar su prefill/lock al resto del formulario. Se instancia una única vez
 * (en `EntryRegistrationForm`) y se comparte entre el paso "Tipo de autorización" (que sólo
 * necesita la búsqueda de empresa) y el paso "Autorizaciones" (que necesita todo lo demás), para
 * que ambos lean y escriban el mismo estado en vez de tener cada uno el suyo. */
export const useTransportAuthorizationFlow = (mode?: RegistrationMode) => {
  const { setValue, watch, register, unregister, getValues, trigger } =
    useFormContext<EntryRegistrationFormValues>();
  const documentNumber = watch("documentNumber");
  const documentCountry = watch("documentCountry");
  const vehiclePlate = watch("vehiclePlate");
  // Para prefillear "Solicitar autorización": si el guardia ya cargó el remito en Observaciones
  // antes de abrir el modal, que aparezca ahí en vez de pedírselo de nuevo (ver `handleOpenSolicitarModal`).
  const transportNumeroRemito = watch("transportNumeroRemito");
  const entranceEvent = watch("entranceEvent");
  const trailerVehicles = watch("trailerVehicles");
  const exitUnitsPreselected = watch("exitUnitsPreselected");

  const { companies, search, setSearch, createCompany } = useTransportCompanies();

  // El flujo lo decide el modo de navegación: no hay toggle, entrar y salir son pantallas distintas.
  const flujo: TransportFlow = mode === "exit" ? "salida" : "entrada";
  const [selectedEmpresa, setSelectedEmpresa] = React.useState<TransportCompany | null>(null);
  const [toast, setToast] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "error" });
  const [nuevaEmpresaModal, setNuevaEmpresaModal] = React.useState<{
    open: boolean;
    prefillNombre: string;
  }>({ open: false, prefillNombre: "" });
  // Puramente visual: qué tarjeta de empresa está desplegada. Independiente de qué autorización
  // está tildada — ocultar/mostrar una tarjeta nunca debe destildar la autorización elegida.
  const [expandedGroupId, setExpandedGroupId] = React.useState<string | null>(null);
  const shouldAutoExpandFirstGroupRef = React.useRef(true);
  const approvedRequestIdsRef = React.useRef<Set<string>>(new Set());
  const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null);
  const [selectedAutorizacionEgresoId, setSelectedAutorizacionEgresoId] = React.useState<
    string | null
  >(null);
  // "Reenviar notificación" no expone el nuevo estado en la respuesta: sólo reinicia el
  // contador local hasta que el próximo refetch traiga el status actualizado.
  const [resentAt, setResentAt] = React.useState<Record<string, number>>({});
  const [isSolicitarModalOpen, setIsSolicitarModalOpen] = React.useState(false);
  const [solicitarModalPrefillCompany, setSolicitarModalPrefillCompany] = React.useState<
    AuthorizingCompany | undefined
  >(undefined);
  // Preautorización paraguas bajo la que se está creando el pedido, si el modal se abrió
  // eligiéndola. También marca la selección como "sin resolver": cerrar el modal la destilda.
  const [solicitarModalParentId, setSolicitarModalParentId] = React.useState<string | null>(null);
  // Autorización recién creada y ya aprobada, a la espera de aparecer en el refetch para
  // seleccionarla automáticamente.
  const [pendingSelectAuthId, setPendingSelectAuthId] = React.useState<string | null>(null);
  // Pedidos de dock creados desde *este* formulario, para fusionarlos con su preautorización.
  // Es estado del componente a propósito: el backend le devuelve el mismo hijo a todos los
  // guardias, así que fusionar según los datos le mostraría a uno el pedido que hizo otro.
  const [dockRequestByParent, setDockRequestByParent] = React.useState<Record<string, string>>({});
  // Id de la autorización pendiente que el guardia está por aprobar por otro medio.
  const [registrarAuthId, setRegistrarAuthId] = React.useState<string | null>(null);

  // El egreso se le pide a la misma empresa que autorizó el ingreso.
  const entranceAuthorizingCompanyId = React.useMemo(
    () => resolveEntranceAuthorizingCompanyId(entranceEvent),
    [entranceEvent]
  );

  // El camión egresa con las patentes con las que entró (salvo el acoplado si quedó desenganchado).
  const entranceVehiclePrefill = React.useMemo(
    () => resolveEntranceVehiclePrefill(entranceEvent),
    [entranceEvent]
  );

  // Las patentes de la autorización de egreso salen de lo elegido en el modal de salida, si vino
  // de ahí; si no, de las del ingreso.
  const authorizationVehiclePrefill = React.useMemo(
    () =>
      resolveExitVehiclePrefill(entranceVehiclePrefill, {
        exitUnitsPreselected: mode === "exit" && exitUnitsPreselected,
        vehiclePlate,
        trailerVehicles,
      }),
    [mode, exitUnitsPreselected, trailerVehicles, vehiclePlate, entranceVehiclePrefill]
  );

  const eventType: TransportEventType = flujo === "salida" ? "out" : "in";
  const {
    authorizations,
    isLoading,
    error,
    refetch,
    requestAuthorization,
    resendAuthorization,
    approveAuthorization,
  } = useTransportAuthorizations({
    transportCompany: selectedEmpresa?.id ?? null,
    eventType,
    driverDni: documentNumber,
  });

  const entryGroups: AuthorizationGroup[] = React.useMemo(() => {
    const groups = buildAuthorizationGroups(authorizations);
    const withResent =
      Object.keys(resentAt).length === 0
        ? groups
        : groups.map((group) => ({
            ...group,
            rows: group.rows.map((row) =>
              resentAt[row.id] ? { ...row, requestedAt: resentAt[row.id] } : row
            ),
          }));
    return mergeLocalDockRequests(withResent, dockRequestByParent);
  }, [authorizations, resentAt, dockRequestByParent]);

  React.useEffect(() => {
    if (!shouldAutoExpandFirstGroupRef.current || entryGroups.length === 0) return;

    setExpandedGroupId(entryGroups[0].id);
    shouldAutoExpandFirstGroupRef.current = false;
  }, [entryGroups]);

  React.useEffect(() => {
    let newlyApprovedGroupId: string | null = null;

    entryGroups.forEach((group) => {
      group.rows.forEach((row) => {
        const isNewlyApproved =
          row.kind === "request" &&
          row.status === "approved" &&
          !approvedRequestIdsRef.current.has(row.id);

        if (isNewlyApproved && !newlyApprovedGroupId) {
          newlyApprovedGroupId = group.id;
        }
        if (row.kind === "request" && row.status === "approved") {
          approvedRequestIdsRef.current.add(row.id);
        }
      });
    });

    if (newlyApprovedGroupId) setExpandedGroupId(newlyApprovedGroupId);
  }, [entryGroups]);

  const exitGroups = React.useMemo(() => {
    const groups = buildExitAuthorizationGroups(authorizations);
    if (Object.keys(resentAt).length === 0) return groups;
    return groups.map((group) => ({
      ...group,
      authorizations: group.authorizations.map((auth) =>
        resentAt[auth.id] ? { ...auth, requestedAt: resentAt[auth.id] } : auth
      ),
    }));
  }, [authorizations, resentAt]);

  // Único lugar que escribe el prefill/lock de la autorización. Recibe `null` para limpiar todo.
  // Sólo escribe (y bloquea) los campos que la autorización realmente define: lo que no viene
  // — marca, seguro, VTV, estadía, flota — queda editable para que lo complete el guardia. Las
  // notas del ingreso quedan afuera: son un campo local del guardia, así que ni se prefillean con
  // las observaciones de la autorización ni se limpian al cambiar de autorización.
  const applyAuthorizationPrefill = React.useCallback(
    (source: AuthorizationPrefillSource | null) => {
      if (!source) {
        setValue("transportAuthPrefill", null);
        setValue("transportLockedFields", []);
        setValue("mercaderiaEnTransito", false);
        setValue("transportNumeroRemito", "");
        setValue("transportRemitoNoAplica", false);
        setValue("transportPrecinto", "");
        return;
      }

      const locked: string[] = [];

      // `false` es un valor autorizado real (no "sin especificar"), a diferencia de un remito
      // vacío: se escribe y se bloquea igual.
      if (source.merchandiseInTransit !== undefined && source.merchandiseInTransit !== null) {
        setValue("mercaderiaEnTransito", source.merchandiseInTransit);
        locked.push("mercaderiaEnTransito");
      }
      if (source.receipt) {
        setValue("transportNumeroRemito", source.receipt);
        setValue("transportRemitoNoAplica", false);
        locked.push("transportNumeroRemito");
      }
      if (source.seal) {
        setValue("transportPrecinto", source.seal);
        locked.push("transportPrecinto");
      }
      if (source.vehiclePlate) {
        locked.push("vehiclePlate");
      }

      setValue("transportAuthPrefill", {
        vehiclePlate: source.vehiclePlate ?? "",
        trailerPlate: source.trailerPlate ?? null,
      });
      setValue("transportLockedFields", locked);
    },
    [setValue]
  );

  const clearAuthorizationSelections = React.useCallback(() => {
    setExpandedGroupId(null);
    setSelectedRowId(null);

    setValue("transportAutorizacion", null);
    setValue("transportAutorizacionEgreso", null);
    setValue("transportAuthorizers", []);
    setValue("transportAutorizacionFull", null);

    applyAuthorizationPrefill(null);
    setSelectedAutorizacionEgresoId(null);
    setSolicitarModalParentId(null);
    setPendingSelectAuthId(null);
  }, [applyAuthorizationPrefill, setValue]);

  // `transportFlujo` sigue al modo de navegación. Escribirlo acá también arregla que quedaba en ""
  // hasta que se tocaba el flujo, con lo cual la tarjeta de autorización del acompañante nunca
  // llegaba a renderizarse.
  const prevFlujoRef = React.useRef<TransportFlow | null>(null);
  React.useEffect(() => {
    setValue("transportFlujo", flujo);
    if (prevFlujoRef.current && prevFlujoRef.current !== flujo) {
      clearAuthorizationSelections();
    }
    prevFlujoRef.current = flujo;
    // clearAuthorizationSelections cambia con cada selección; sólo queremos reaccionar al modo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flujo, setValue]);

  const handleSelectEmpresa = (empresa: TransportCompany) => {
    shouldAutoExpandFirstGroupRef.current = true;
    setSelectedEmpresa(empresa);
    clearAuthorizationSelections();

    setValue("transportEmpresa", { id: empresa.id, name: empresa.name });
  };

  const handleClearEmpresa = () => {
    shouldAutoExpandFirstGroupRef.current = false;
    setSelectedEmpresa(null);
    setSearch("");
    clearAuthorizationSelections();

    setValue("transportEmpresa", null);
  };

  // En egreso la empresa ya la sabemos: es con la que entró el camión. Se prefillea una sola vez
  // para que la lista de autorizaciones de salida cargue sola; el guardia la puede limpiar y elegir
  // otra, y por eso no se vuelve a sembrar.
  const empresaSeededRef = React.useRef(false);
  React.useEffect(() => {
    if (mode !== "exit" || empresaSeededRef.current || selectedEmpresa) return;

    const entrance = entranceEvent?.event || entranceEvent;
    const entryAuth = entrance?.transportAuthorizations?.[0];
    if (!entryAuth?.transportCompany) return;

    empresaSeededRef.current = true;
    shouldAutoExpandFirstGroupRef.current = true;
    setSelectedEmpresa({
      id: entryAuth.transportCompany,
      name: entryAuth.transportCompanyName || "",
      description: "",
      contactEmails: [],
    });
    setValue("transportEmpresa", {
      id: entryAuth.transportCompany,
      name: entryAuth.transportCompanyName || "",
    });
  }, [mode, entranceEvent, selectedEmpresa, setValue]);

  // El acoplado del ingreso se refleja como vehículo asociado del egreso. Se siembra una sola vez,
  // como la empresa: si el guardia lo borra porque el camión sale sin él, no se lo volvemos a poner.
  const acopladoSeededRef = React.useRef(false);
  React.useEffect(() => {
    if (mode !== "exit" || acopladoSeededRef.current) return;

    // Si el egreso salió del modal de salida, las unidades ya están elegidas: una lista vacía
    // significa que el guardia destildó los acoplados, no que falte completarla.
    if (getValues("exitUnitsPreselected")) return;

    // Sólo el primero: sembrar los demás acoplados en "Vehículos Asociados" queda fuera de este
    // cambio, que se limita al modal de solicitud de autorización de egreso.
    const [trailerPlate] = entranceVehiclePrefill.trailerPlates;
    const [trailer] = entranceVehiclePrefill.trailers;
    if (!trailerPlate) return;
    if ((getValues("trailerVehicles") || []).length > 0) return;

    acopladoSeededRef.current = true;
    setValue("trailerVehicles", [
      {
        // El id evita que el submit dé de alta un acoplado repetido: `resolveTrailerVehicles` lo
        // reusa tal cual y sólo cae a `addVehicle` cuando no lo tiene.
        ...(trailer?.id ? { id: trailer.id } : {}),
        vehiclePlate: trailerPlate,
        vehicleBrand: trailer?.brand || "",
        vehicleColor: trailer?.color || "",
        insuranceCompany: trailer?.insuranceCompanyId || "",
        policyNumber: trailer?.insurancePolicy || "",
        insuranceExpirationDate: parseApiOrIsoDate(trailer?.insuranceExpirationDate),
        appliesVtv: !!trailer?.technicalReviewExpirationDate,
        vtvExpirationDate: parseApiOrIsoDate(trailer?.technicalReviewExpirationDate),
      },
    ]);
  }, [mode, entranceVehiclePrefill, getValues, setValue]);

  const handleCrearEmpresa = async (data: NuevaEmpresaData): Promise<boolean> => {
    const result = await createCompany(data.name);
    if (result.success) {
      setSearch("");
      handleSelectEmpresa(result.company);
      return true;
    }
    setToast({ open: true, message: result.error, severity: "error" });
    return false;
  };

  React.useEffect(() => {
    if (error) {
      setToast({ open: true, message: error, severity: "error" });
    }
  }, [error]);

  const handleResend = async (authId: string) => {
    const result = await resendAuthorization(authId);
    if (!result.success) {
      setToast({ open: true, message: result.error, severity: "error" });
      return;
    }
    setResentAt((prev) => ({ ...prev, [authId]: Date.now() }));
  };

  // La observación del alta se muestra como referencia: al aprobar, la nueva la pisa.
  const registrarAuthObservations = React.useMemo(() => {
    if (!registrarAuthId) return null;
    const row = entryGroups
      .flatMap((group) => group.rows)
      .find((candidate) => candidate.id === registrarAuthId);
    if (row) return row.gateObservations ?? null;
    const exitAuth = exitGroups
      .flatMap((group) => group.authorizations)
      .find((candidate) => candidate.id === registrarAuthId);
    return exitAuth?.gateObservations ?? null;
  }, [registrarAuthId, entryGroups, exitGroups]);

  const handleRegistrarAutorizacion = async (
    data: RegistrarAutorizacionSubmitData
  ): Promise<boolean> => {
    if (!registrarAuthId) return false;
    const result = await approveAuthorization({ id: registrarAuthId, ...data });
    if (!result.success) {
      setToast({ open: true, message: result.error, severity: "error" });
      return false;
    }
    setToast({ open: true, message: "Autorización registrada", severity: "success" });
    // Recién aprobada no queda seleccionada sola: el guardia ve el toast de éxito y puede dar
    // por hecho el paso. La marcamos pendiente para que el mismo mecanismo que auto-selecciona
    // las autoaprobadas la tilde apenas el refetch la traiga actualizada.
    setPendingSelectAuthId(registrarAuthId);
    return true;
  };

  /** Con `approval` la solicitud nace aprobada por el propio guardia; sin él, queda pendiente de
   * los managers. Es el único campo que cambia entre los dos flujos. */
  const selfApproveFields = (approval?: RegistrarAutorizacionSubmitData) =>
    approval ? { selfApprove: true, ...approval } : {};

  const requestToastMessage = (approval?: RegistrarAutorizacionSubmitData) =>
    approval ? "Autorización registrada" : "Autorización solicitada";

  const handleRequestEntryAuthorization = async (
    data: SolicitarAutorizacionSubmitData,
    approval?: RegistrarAutorizacionSubmitData
  ): Promise<boolean> => {
    if (!selectedEmpresa) return false;
    const result = await requestAuthorization({
      company: data.company,
      transportCompany: selectedEmpresa.id,
      eventType: "in",
      operationType: data.operationType,
      receipt: data.receipt || undefined,
      driverDni: documentNumber,
      driverCountryCode: documentCountry || "ARG",
      vehiclePlate: data.vehiclePlate,
      parentAuthorization: solicitarModalParentId ?? undefined,
      observations: data.observations || undefined,
      // La observación del registro por otro medio pisa a la del alta: va después del spread.
      ...selfApproveFields(approval),
    });
    if (!result.success) {
      setToast({ open: true, message: result.error, severity: "error" });
      return false;
    }
    setToast({ open: true, message: requestToastMessage(approval), severity: "success" });
    // La preautorización era sólo el punto de partida: lo que se usa para entrar es el pedido
    // hijo. Se destilda, y desde acá en más las dos se muestran como una sola tarjeta.
    if (solicitarModalParentId) {
      deselectRow();
      setDockRequestByParent((prev) => ({ ...prev, [solicitarModalParentId]: result.id }));
      setSolicitarModalParentId(null);
    }
    if (approval) {
      setPendingSelectAuthId(result.id);
    }
    return true;
  };

  const handleRequestExitAuthorization = async (
    data: SolicitarAutorizacionEgresoSubmitData,
    approval?: RegistrarAutorizacionSubmitData
  ): Promise<boolean> => {
    if (!selectedEmpresa) return false;
    const result = await requestAuthorization({
      company: data.company,
      transportCompany: selectedEmpresa.id,
      eventType: "out",
      driverDni: documentNumber,
      driverCountryCode: documentCountry || "ARG",
      vehiclePlate: data.vehiclePlate,
      trailerPlates: data.trailerPlates.length > 0 ? data.trailerPlates : undefined,
      trailerNotApplicable: data.trailerNotApplicable,
      loaded: data.loaded,
      receipt: data.receipt || undefined,
      seal: data.seal || undefined,
      merchandiseInTransit: data.merchandiseInTransit,
      controlChecks: data.controlChecks,
      vehicleCustodyPlate: data.vehicleCustodyPlate || undefined,
      vehicleCustodyCompany: data.vehicleCustodyCompany || undefined,
      observations: data.observations || undefined,
      // La observación del registro por otro medio pisa a la del alta: va después del spread.
      ...selfApproveFields(approval),
    });
    if (!result.success) {
      setToast({ open: true, message: result.error, severity: "error" });
      return false;
    }
    setToast({ open: true, message: requestToastMessage(approval), severity: "success" });
    // Igual que en entrada: si nace ya autoaprobada, todavía no queda seleccionada — hay que
    // esperar a que el refetch la traiga para poder tildarla.
    if (approval) {
      setPendingSelectAuthId(result.id);
    }
    return true;
  };

  // Sólo pliega/despliega la tarjeta de la empresa: mostrar u ocultar información no tiene que
  // tocar la autorización tildada (ver selectRow/deselectRow, que son los únicos que deben
  // hacerlo). Antes este handler también deseleccionaba al plegar, perdiendo el prefill de
  // vehículo/acoplado ya cargado en el resto del formulario.
  const handleToggleExpandGroup = (group: AuthorizationGroup) => {
    setExpandedGroupId((prev) => (prev === group.id ? null : group.id));
  };

  // En entrada el camión ingresa con un pedido, nunca con la preautorización paraguas: si el
  // guardia eligió una y cerró el modal sin crear el pedido hijo, el formulario no puede enviarse.
  // En egreso la autorización sí vale por sí misma: alcanza con que haya una elegida.
  //
  // El campo no guarda nada: es sólo el anclaje del error (lo lee `MissingFieldsSummary` y le da a
  // `focusField` su `data-field-name`). La regla mira directamente lo elegido en cada flujo — lo
  // mismo que lee `useEvent` para armar el payload — en vez de un espejo que se puede desincronizar.
  React.useEffect(() => {
    register("transportAuthorizationSelected", {
      validate: () => {
        if (flujo === "salida") {
          return (
            !!getValues("transportAutorizacionEgreso") ||
            "Debe seleccionar una autorización de acceso"
          );
        }
        const row = getValues("transportAutorizacionFull")?.rows?.[0];
        return (!!row && row.kind === "request") || "Debe seleccionar una autorización de acceso";
      },
    });
    return () => {
      unregister("transportAuthorizationSelected");
    };
  }, [flujo, register, unregister, getValues]);

  const handleExitControlsChange = React.useCallback(
    (controls: TransportExitControls | null) => {
      setValue("transportExitControls", controls);
    },
    [setValue]
  );

  const handleSelectAutorizacionEgreso = (auth: ExitAuthorization) => {
    if (selectedAutorizacionEgresoId === auth.id) {
      // Close/Deselect
      setSelectedAutorizacionEgresoId(null);

      setValue("transportAutorizacionEgreso", null);
      applyAuthorizationPrefill(null);
    } else {
      // Open/Select
      setSelectedAutorizacionEgresoId(auth.id);

      setValue("transportAutorizacionEgreso", auth);
      applyAuthorizationPrefill(auth);
    }
    // Revalidamos para que el error se limpie/reaparezca apenas cambia la selección, sin esperar
    // a otro submit.
    trigger("transportAuthorizationSelected");
  };

  const deselectRow = React.useCallback(() => {
    setSelectedRowId(null);
    setValue("transportAuthorizers", []);
    setValue("transportAutorizacionFull", null);
    applyAuthorizationPrefill(null);
    trigger("transportAuthorizationSelected");
  }, [applyAuthorizationPrefill, setValue, trigger]);

  const selectRow = React.useCallback(
    (group: AuthorizationGroup, row: AuthorizationRow) => {
      setExpandedGroupId(group.id);
      setSelectedRowId(row.id);
      applyAuthorizationPrefill(row);
      setValue(
        "transportAuthorizers",
        row.people.map((person) => ({
          name: person.name,
          phone: person.phone,
          role: row.operationLabel,
        }))
      );
      setValue("transportAutorizacionFull", { ...group, rows: [row] });
      // Una preautorización todavía no habilita el ingreso: recién lo hace el pedido hijo, que
      // `mergeLocalDockRequests` pone en esta misma fila al crearse. Revalidamos para que el error
      // se limpie apenas se elige una fila válida, sin esperar a otro submit.
      trigger("transportAuthorizationSelected");
    },
    [applyAuthorizationPrefill, setValue, trigger]
  );

  const handleSelectRow = (groupId: string, rowId: string) => {
    const group = entryGroups.find((g) => g.id === groupId);
    const row = group?.rows.find((r) => r.id === rowId);
    if (!group || !row) return;

    if (selectedRowId === rowId) {
      deselectRow();
      return;
    }

    selectRow(group, row);

    // Una preautorización es el paraguas: el camión no entra con ella sino con un pedido hijo
    // que la referencia. Elegirla abre el modal de solicitud ya prefilleado; si el guardia lo
    // cierra sin crear nada, la fila se destilda.
    if (row.kind === "authorization") {
      setSolicitarModalParentId(row.id);
      // La preautorización ya sabe a qué empresa pertenece: no tiene sentido volver a
      // preguntársela al guardia, pero la sigue pudiendo cambiar en el modal.
      setSolicitarModalPrefillCompany({ id: group.companyId, name: group.title });
      setIsSolicitarModalOpen(true);
    }
  };

  // La autorización recién auto-aprobada (por autoaprobación o por "Registrar por otro medio")
  // nace `approved`, pero aparece en la lista recién con el refetch que dispara la acción: hay
  // que esperarla para poder seleccionarla.
  React.useEffect(() => {
    if (!pendingSelectAuthId) return;
    if (flujo === "salida") {
      for (const group of exitGroups) {
        const auth = group.authorizations.find((a) => a.id === pendingSelectAuthId);
        if (auth) {
          handleSelectAutorizacionEgreso(auth);
          setPendingSelectAuthId(null);
          return;
        }
      }
      return;
    }
    for (const group of entryGroups) {
      const row = group.rows.find((r) => r.id === pendingSelectAuthId);
      if (row) {
        selectRow(group, row);
        setPendingSelectAuthId(null);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryGroups, exitGroups, pendingSelectAuthId, selectRow, flujo]);

  const handleOpenSolicitarModal = () => {
    setSolicitarModalParentId(null);
    setSolicitarModalPrefillCompany(undefined);
    setIsSolicitarModalOpen(true);
  };

  const handleCloseSolicitarModal = () => {
    // Cerrar sin crear nada (cancelar o backdrop) deja la preautorización sin pedido hijo, así
    // que tampoco tiene sentido dejarla tildada.
    const hadParent = !!solicitarModalParentId;
    setIsSolicitarModalOpen(false);
    setSolicitarModalParentId(null);
    setSolicitarModalPrefillCompany(undefined);
    if (hadParent) {
      deselectRow();
    }
  };

  return {
    // Empresa (usado por el paso 2, "Tipo de autorización")
    search,
    setSearch,
    companies,
    selectedEmpresa,
    handleSelectEmpresa,
    handleClearEmpresa,
    handleCrearEmpresa,
    nuevaEmpresaModal,
    setNuevaEmpresaModal,

    // Compartido
    flujo,
    toast,
    setToast,

    // Autorizaciones (usado por el paso 5, "Autorizaciones")
    isLoading,
    entryGroups,
    exitGroups,
    expandedGroupId,
    handleToggleExpandGroup,
    selectedRowId,
    handleSelectRow,
    handleResend,
    onRefresh: () => refetch({ silent: true }),
    vehiclePlate,
    transportNumeroRemito,
    isSolicitarModalOpen,
    handleOpenSolicitarModal,
    handleCloseSolicitarModal,
    solicitarModalPrefillCompany,
    handleRequestEntryAuthorization,
    selectedAutorizacionEgresoId,
    handleSelectAutorizacionEgreso,
    entranceAuthorizingCompanyId,
    authorizationVehiclePrefill,
    handleRequestExitAuthorization,
    handleExitControlsChange,
    registrarAuthId,
    setRegistrarAuthId,
    registrarAuthObservations,
    handleRegistrarAutorizacion,
  };
};

export type TransportAuthorizationFlow = ReturnType<typeof useTransportAuthorizationFlow>;
